import * as _ from "lodash";
import * as moment from "moment";
import {Assessment} from "./Assessment";
import {Application} from "./Application";
import {Credit} from "./Credit";
import {isSystemAccepted} from "./ApplicationStatusUtil";

export class ApplicationCreditSummary {
    public application: Application;
    public assessments: Assessment[];
    public approvedAssessments: Assessment[];
    public unAcceptedAssessments: Assessment[];
    public creditPoints: number;
    public creditPointsOffered: number;
    public studentResponses: Map<string, string>;
    public faculty: string;
    public readonly: boolean;
    public detailsVisible: boolean;
    public isActionable: boolean;

    constructor(application: Application, assessments: Assessment[]) {
        this.application = application;
        this.assessments = assessments;

        /***
         * After student successfully accepts the credit, the application summary should be readonly.
         * However, the application status will become SYSTEM_ACCEPTED as well which leads to be NOT readonly.
         * So this.readonly needs to be justified specifically for applications which are accepted by students.
         */
        const isSystemAcceptedStatus = isSystemAccepted(this.application.status);
        this.readonly = !(this.application.status === "APPROVED" || isSystemAcceptedStatus); // TODO: APPROVED can be removed after migration
        this.detailsVisible = _.includes(["APPROVED", // TODO: this can be removed after migration
            "STUDENT_ACCEPTED", "STUDENT_DECLINED", "DECLINED"], this.application.status) || isSystemAcceptedStatus;
        this.isActionable = isSystemAcceptedStatus;

        this.application.coursesApplied.forEach((courseApplied) => {
            this.faculty = courseApplied.course.faculty;
        });

        this.approvedAssessments = this.assessments.filter((assessment) => {
            return assessment.status === "APPROVED";
        });

        // this includes unaccepted assessments and declined ones
        this.unAcceptedAssessments = this.assessments.filter((assessment) => {
           return !assessment.studentAccepted;
        });

        // count how many assessments are accepted by students
        let studentAcceptedAssessment = 0;
        this.assessments.forEach((assessment) => {
            assessment.beforeExpiry = !assessment.expirableCredit ||
                moment().isBefore(moment(application.approvedDate).add(assessment.offerExpiryOffset, "weeks"));

            if(assessment.studentAccepted) {
                studentAcceptedAssessment ++;
            }
        });

        // if all the assessments are accepted, the application summary should be readonly
        if(studentAcceptedAssessment === assessments.length) {
            this.readonly = true;
        }

        this.studentResponses = {};
        this.reset();

        this.calculateCreditPoints();
    }

    public selectAllCredits() {
        this.approvedAssessments
            .forEach((assessment) => {
                this.studentResponses[assessment.id] = Assessment.STUDENT_ACCEPTED;
            });

        this.calculateCreditPoints();
    }

    public selectedCredits(): Credit[] {
        return _.flatMap(this.approvedAssessments.filter((assessment) =>
            this.studentResponses[assessment.id] === Assessment.STUDENT_ACCEPTED
        ), ((assessment) => assessment.credits));
    }

    public reset() {
        this.approvedAssessments
            .forEach((assessment) => {
                this.studentResponses[assessment.id] = !this.readonly ? Assessment.STUDENT_NO_RESPONSE :
                    (assessment.studentAccepted ? Assessment.STUDENT_ACCEPTED : Assessment.STUDENT_DECLINED);
            });
        this.calculateCreditPoints();
    }

    public isPristine() {
        return this.readonly ||
            this.approvedAssessments
                .filter((assessment) => {
                    return this.studentResponses[assessment.id] !== Assessment.STUDENT_NO_RESPONSE;
                })
                .length === 0;
    }

    public isFilledIn(): boolean {
        // the unAcceptedAssessments includes all the assessments, even if the assessment is rejected
        return this.unAcceptedAssessments
            .filter((assessment) => {
                return assessment.status == "APPROVED" &&
                    this.studentResponses[assessment.id] !== Assessment.STUDENT_ACCEPTED &&
                    this.studentResponses[assessment.id] !== Assessment.STUDENT_DECLINED;
            })
            .length === 0;
    }

    public someAccepted(): boolean {
        return this.approvedAssessments
            .filter((assessment) => {
                return this.studentResponses[assessment.id] === Assessment.STUDENT_ACCEPTED;
            })
            .length > 0;
    }

    private calculateCreditPoints() {
        this.creditPointsOffered = this.assessments
            .filter((assessment) => assessment.status === "APPROVED")
            .reduce((result, assessment) => result + assessment.credits.reduce((count, credit) => credit.type === "SPECIFIED_CREDIT" && credit.specifiedCreditType === "PRECLUSION" ? count : count + credit.creditPoints, 0), 0);

        this.creditPoints = this.approvedAssessments
            .filter((assessment) => assessment.studentAccepted)
            .reduce((result, assessment) => result + assessment.credits.reduce((count, credit) => credit.type === "SPECIFIED_CREDIT" && credit.specifiedCreditType === "PRECLUSION" ? count : count + credit.creditPoints, 0), 0);
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/main/client/app/shared/model/ApplicationCreditSummary.ts